/**
 * دوالّ السيرفر لبلوكات صفحة «منتج جديد» (`/new-product`).
 * القراءة عامّة (الصفحة العامّة)، والكتابة للأدمن فقط.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { TEXT_POSITIONS } from "@/lib/text-position";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";
import {
  normalizeNewProductRow,
  normalizeNewProductPageRow,
  type NewProductBlock,
  type NewProductPage,
} from "./newProductBlocks.server";

export type { NewProductBlock, NewProductPage };

const BlockInput = z.object({
  id: z.string().uuid().optional(),
  pageId: z.string().uuid(),
  sort: z.number().int().min(0).max(999).default(0),
  enabled: z.boolean().default(true),
  device: z.enum(["all", "mobile", "desktop"]).default("all"),
  type: z.enum(["media", "text"]).default("media"),
  mediaUrl: z.string().max(800).default(""),
  mobileMediaUrl: z.string().max(800).default(""),
  posterUrl: z.string().max(800).default(""),
  titleAr: z.string().max(240).default(""),
  titleEn: z.string().max(240).default(""),
  bodyAr: z.string().max(4000).default(""),
  bodyEn: z.string().max(4000).default(""),
  ctaLabelAr: z.string().max(80).default(""),
  ctaLabelEn: z.string().max(80).default(""),
  ctaHref: z.string().max(600).default(""),
  textPosition: z.enum(TEXT_POSITIONS).default("center"),
  overlayOpacity: z.number().int().min(0).max(100).default(30),
  fullHeight: z.boolean().default(false),
});

/** قراءة عامّة — البلوكات المفعَّلة فقط (للصفحة العامّة). */
export const getNewProductBlocks = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ slug: z.string().max(120).default("main") }).parse(d ?? {}),
  )
  .handler(async ({ data: input }): Promise<{ blocks: NewProductBlock[] }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: page } = await supabaseAdmin
      .from("new_product_pages")
      .select("id")
      .eq("slug", input.slug)
      .eq("enabled", true)
      .maybeSingle();
    if (!page) return { blocks: [] };
    const { data } = await supabaseAdmin
      .from("new_product_blocks")
      .select("*")
      .eq("page_id", (page as { id: string }).id)
      .eq("enabled", true)
      .order("sort", { ascending: true })
      .order("created_at", { ascending: true });
    return { blocks: (data ?? []).map((r) => normalizeNewProductRow(r as Record<string, unknown>)) };
  });

/** قراءة الأدمن — كلّ البلوكات بما فيها المعطَّلة. */
export const listNewProductBlocksAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ blocks: NewProductBlock[] }> => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("new_product_blocks")
      .select("*")
      .order("sort", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return { blocks: (data ?? []).map((r) => normalizeNewProductRow(r as Record<string, unknown>)) };
  });

export const upsertNewProductBlock = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => BlockInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const row = {
      page_id: data.pageId,
      sort: data.sort,
      enabled: data.enabled,
      device: data.device,
      type: data.type,
      media_url: data.mediaUrl.trim(),
      mobile_media_url: data.mobileMediaUrl.trim(),
      poster_url: data.posterUrl.trim(),
      title_ar: data.titleAr.trim(),
      title_en: data.titleEn.trim(),
      body_ar: data.bodyAr,
      body_en: data.bodyEn,
      cta_label_ar: data.ctaLabelAr.trim(),
      cta_label_en: data.ctaLabelEn.trim(),
      cta_href: data.ctaHref.trim(),
      text_position: data.textPosition,
      overlay_opacity: data.overlayOpacity,
      full_height: data.fullHeight,
      updated_at: new Date().toISOString(),
    };
    if (data.id) {
      const { error } = await supabaseAdmin
        .from("new_product_blocks")
        .update(row)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }
    const { data: inserted, error } = await supabaseAdmin
      .from("new_product_blocks")
      .insert(row)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: inserted.id as string };
  });

export const deleteNewProductBlock = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("new_product_blocks")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderNewProductBlocks = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ ids: z.array(z.string().uuid()).max(60) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    for (let i = 0; i < data.ids.length; i++) {
      const { error } = await supabaseAdmin
        .from("new_product_blocks")
        .update({ sort: i, updated_at: new Date().toISOString() })
        .eq("id", data.ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });


/* ────────── صفحات «منتج جديد» (كلّ منتج باسمه) ────────── */

const PageInput = z.object({
  id: z.string().uuid().optional(),
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9-]+$/, "المعرّف بحروف إنجليزيّة صغيرة وأرقام وشرطات فقط"),
  nameAr: z.string().max(160).default(""),
  nameEn: z.string().max(160).default(""),
  productHandle: z.string().max(200).default(""),
  coverUrl: z.string().max(800).default(""),
  enabled: z.boolean().default(true),
  sort: z.number().int().min(0).max(999).default(0),
});

/** قراءة عامّة — الصفحات المفعَّلة فقط. */
export const getNewProductPages = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ pages: NewProductPage[] }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("new_product_pages")
      .select("*")
      .eq("enabled", true)
      .order("sort", { ascending: true })
      .order("created_at", { ascending: true });
    return {
      pages: (data ?? []).map((r) => normalizeNewProductPageRow(r as Record<string, unknown>)),
    };
  },
);

export const listNewProductPagesAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ pages: NewProductPage[] }> => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("new_product_pages")
      .select("*")
      .order("sort", { ascending: true })
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    return {
      pages: (data ?? []).map((r) => normalizeNewProductPageRow(r as Record<string, unknown>)),
    };
  });

export const upsertNewProductPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => PageInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const row = {
      slug: data.slug.trim(),
      name_ar: data.nameAr.trim(),
      name_en: data.nameEn.trim(),
      product_handle: data.productHandle.trim(),
      cover_url: data.coverUrl.trim(),
      enabled: data.enabled,
      sort: data.sort,
      updated_at: new Date().toISOString(),
    };
    if (data.id) {
      const { error } = await supabaseAdmin
        .from("new_product_pages")
        .update(row)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true, id: data.id };
    }
    const { data: inserted, error } = await supabaseAdmin
      .from("new_product_pages")
      .insert(row)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: inserted.id as string };
  });

export const deleteNewProductPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("new_product_pages")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
